import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../firebase';
import BackButton from '../components/BackButton';

// SmartFinance - detail page for the SmartFinance tile. v1 for two personas:
// SMB owner (never touches client_gid, screen looks exactly as simple as
// before) and accountant (multi-tenant, scopes Statement/Extract to a
// client_gid). Design brief: 2026-07-27 (post accountant-market session).
// Bugfix pass: 2026-07-27 evening (stale error state, escape-rendering bug,
// quiet auto-extract per live testing feedback).
//
// ── What actually takes client_gid (per the backend brief's explicit list) ──
//   Statement  {from, to, client_gid?}        - YES
//   Extract    {file_id, client_gid?}         - YES
//   Confirm    {entry_id}                     - NO (entry already carries
//                                                 the client attribution
//                                                 from the Extract call)
//   Ask        {question, from, to}           - UNCONFIRMED. Not in the
//     brief's explicit two-endpoint list. Deliberately NOT sending client_gid
//     here rather than guessing - flag to backend: does an accountant need
//     Ask scoped to a client too?
//   Upload/notify_upload                      - NOT sending client_gid here
//     either. The brief's top-line contract names only Statement + Extract;
//     the parenthetical on Upload was ambiguous. Attribution happens at
//     Extract time instead, which IS in the confirmed contract.
//
// Auth (unchanged, all 4 sf-*-http functions): Authorization: Bearer
// <firebase_id_token>, Content-Type: application/json, origin
// portal.dbos.world, never gido_id in any body (server-derives it from the
// token; a client_gid can only ever partition within that same gido_id -
// enforced backend-side, never trust the frontend for that boundary).
//
// ── No "list my clients" endpoint exists yet (flagged by backend) ──
// The client switcher is therefore a manual client_gid field + a browser-
// local "recent clients" list (localStorage, keyed per logged-in GIDO) -
// a real convenience, honestly NOT a backend-sourced directory. Flagged
// back to backend as a small future add (distinct-client_gid query).
//
// ── Upload/Extract/Confirm collapse into one Intake flow ──
// Multi-file drop -> each file auto-attempts Extract quietly in the
// background. OCR/labeling is async on the backend, so "not ready yet"
// (422 reason "not_extractable") is NOT shown to the user as an error and
// has NO retry button - it is a normal, expected, invisible waiting state.
// The item just shows a calm "Reading your document..." and retries itself
// on a slow backoff, indefinitely. Only a GENUINELY different failure
// (cross_domain_needs_grant, not_found, or a real 422 validation problem
// unrelated to OCR timing) surfaces as a visible error with a manual retry -
// those are not transient and hiding them would be dishonest.
// Raw file_id/entry_id fields still exist, collapsed under
// "Advanced / manual entry" for edge cases - not removed, just not in the
// primary path.

const UPLOAD_HANDLER_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-upload-handler';
const NOTIFY_URL         = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-notify-upload';
const FN_BASE             = 'https://europe-west1-dbos-core-prod.cloudfunctions.net';

// Backoff for the quiet, invisible "OCR not ready yet" retry. Grows, then
// caps at the last value and keeps retrying forever at that pace.
const EXTRACT_RETRY_DELAYS_MS = [4000, 8000, 15000, 30000];

async function callSmartFinance(path, body) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${FN_BASE}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

function uploadToGCS(signedUrl, file, mimeType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', mimeType);
    xhr.send(file);
  });
}

function defaultPeriod() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(today) };
}

// ── Client scope bar - hidden complexity for the SMB owner, real switcher
// for the accountant. Nothing renders differently for the SMB-owner default
// state versus the original single-tenant screen. ──────────────────────────
function ClientScopeBar({ gidoId, mode, setMode, clientGid, setClientGid }) {
  const storageKey = `dbos_sf_recent_clients_${gidoId || 'unknown'}`;
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  });

  function remember(gid) {
    if (!gid) return;
    const next = [gid, ...recent.filter((g) => g !== gid)].slice(0, 8);
    setRecent(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) {}
  }

  if (mode === 'own') {
    return (
      <div className="sf-scope-bar">
        <span className="sf-scope-label">Your own books</span>
        <button className="sf-scope-link" onClick={() => setMode('client')}>
          Working on behalf of a client?
        </button>
      </div>
    );
  }

  return (
    <div className="sf-scope-bar sf-scope-bar-client">
      <span className="sf-scope-label">Client:</span>
      <input
        type="text"
        value={clientGid}
        onChange={(e) => setClientGid(e.target.value)}
        onBlur={() => clientGid.trim() && remember(clientGid.trim())}
        placeholder="Client GID (e.g. GID_...)"
        className="sf-scope-input"
      />
      {recent.length > 0 && (
        <div className="sf-scope-chips">
          {recent.map((g) => (
            <button key={g} className={`sf-chip ${g === clientGid ? 'active' : ''}`} onClick={() => setClientGid(g)}>
              {g}
            </button>
          ))}
        </div>
      )}
      <button className="sf-scope-link" onClick={() => { setMode('own'); setClientGid(''); }}>
        Back to my own books
      </button>
      <span className="sf-scope-hint">
        This list is remembered on this device only - not a synced client directory (none exists yet).
      </span>
    </div>
  );
}

function PeriodPicker({ from, to, onChange }) {
  return (
    <div className="sf-period-row">
      <label>From<input type="date" value={from} onChange={(e) => onChange({ from: e.target.value, to })} /></label>
      <label>To<input type="date" value={to} onChange={(e) => onChange({ from, to: e.target.value })} /></label>
    </div>
  );
}

function StatementPanel({ from, to, clientGid }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  async function loadStatement() {
    setLoading(true); setError(null); setResult(null);
    try {
      const body = { from, to };
      if (clientGid?.trim()) body.client_gid = clientGid.trim();
      const { ok, data } = await callSmartFinance('sf-statement-http', body);
      if (ok) setResult(data);
      else setError(data?.error || 'Could not load statement.');
    } catch (e) { setError('Network error. Try again.'); }
    setLoading(false);
  }

  const scopeSuffix = clientGid?.trim() ? ` - ${clientGid.trim()}` : '';

  return (
    <div className="sf-panel">
      <div className="sf-panel-header">
        <h3>📄 Statement{scopeSuffix}</h3>
        <button className="sf-btn" onClick={loadStatement} disabled={loading}>
          {loading ? 'Loading…' : 'Load statement'}
        </button>
      </div>
      {error && <div className="sf-panel-error">{error}</div>}
      {result && <pre className="sf-panel-result">{JSON.stringify(result, null, 2)}</pre>}
      {!error && !result && !loading && (
        <p className="sf-panel-hint">
          Income, expenses and surplus for the period above. Empty until confirmed
          entries exist for this range - that's correct, not a bug.
        </p>
      )}
    </div>
  );
}

function AskPanel({ from, to }) {
  const [question, setQuestion] = useState('');
  const [loading, setLoading]   = useState(false);
  const [answer, setAnswer]     = useState(null);
  const [error, setError]       = useState(null);

  async function ask() {
    if (!question.trim()) return;
    setLoading(true); setError(null); setAnswer(null);
    try {
      // NOT sending client_gid - unconfirmed whether Ask supports client
      // scoping. Flagged to backend rather than guessed.
      const { ok, data } = await callSmartFinance('sf-ask-http', { question: question.trim(), from, to });
      if (ok) setAnswer(data?.answer || JSON.stringify(data));
      else setError(data?.error || 'Could not get an answer.');
    } catch (e) {
      // A fetch-level failure here (vs. a normal ok:false response) is
      // consistent with the backend throwing an unhandled exception before
      // it reaches its own CORS-header code - the browser then reports it
      // as a CORS/network error, but the real fault is server-side (a 500).
      setError('The assistant hit a server error (not a network problem on this end). Backend fix needed.');
    }
    setLoading(false);
  }

  return (
    <div className="sf-panel">
      <div className="sf-panel-header"><h3>💬 Ask</h3></div>
      <div className="sf-ask-row">
        <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && ask()}
               placeholder="e.g. What did I spend on supplies last month?" className="sf-ask-input" />
        <button className="sf-btn" onClick={ask} disabled={loading || !question.trim()}>{loading ? '…' : 'Ask'}</button>
      </div>
      {error && <div className="sf-panel-error">{error}</div>}
      {answer && <div className="sf-panel-answer">{answer}</div>}
      {!error && !answer && !loading && (
        <p className="sf-panel-hint">Ask about entries in the period above, in plain language.</p>
      )}
    </div>
  );
}

// ── Intake: Upload -> quiet auto-Extract -> Accept (Confirm) ───────────────
function IntakePanel({ clientGid, onConfirmed }) {
  const [items, setItems] = useState([]); // {id, name, status, progress, error, fileId, draft, entryId, attempt}
  const inputRef = useRef(null);
  const timers = useRef({});

  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  function patchItem(id, patch) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }

  async function attemptExtract(id, fileId, attempt) {
    // Always clear any stale error/draft from a previous attempt first -
    // otherwise a later success can leave an earlier failure's message
    // visible underneath it.
    patchItem(id, {
      status: attempt === 0 ? 'extracting' : 'processing',
      attempt,
      error: null,
    });
    try {
      const body = { file_id: fileId };
      if (clientGid?.trim()) body.client_gid = clientGid.trim();
      const { ok, status, data } = await callSmartFinance('sf-extract-http', body);

      if (ok) {
        patchItem(id, { status: 'draft_ready', draft: data, entryId: data.entry_id || '', error: null });
        return;
      }

      // "Not ready yet" (OCR/labeling still running) is NORMAL and expected,
      // not an error. Never shown to the user, no button - just retry
      // quietly, forever, on a slow backoff.
      const notReadyYet = data?.error === 'not_extractable' || data?.reason === 'not_extractable';
      if (notReadyYet) {
        const delay = EXTRACT_RETRY_DELAYS_MS[Math.min(attempt, EXTRACT_RETRY_DELAYS_MS.length - 1)];
        timers.current[id] = setTimeout(() => attemptExtract(id, fileId, attempt + 1), delay);
        return; // stays 'processing' - no error, no button
      }

      // Anything else is a genuine, non-transient problem - show it honestly.
      const map = {
        cross_domain_needs_grant: "This file isn't flagged for SmartFinance.",
        not_found:                'File not found.',
      };
      const friendly = map[data?.reason];
      patchItem(id, {
        status: 'extract_error',
        error: friendly || `Could not extract - ${JSON.stringify(data)}`,
      });
    } catch (e) {
      patchItem(id, { status: 'extract_error', error: 'Network error during extraction.' });
    }
  }

  async function handleFiles(fileList) {
    const files = Array.from(fileList);
    for (const file of files) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setItems((prev) => [...prev, { id, name: file.name, status: 'getting_url', progress: 0, error: null }]);
      try {
        const idToken = await auth.currentUser.getIdToken();
        const res = await fetch(UPLOAD_HANDLER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_token: idToken,
            filename: file.name,
            mime_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to get upload URL');

        patchItem(id, { status: 'uploading' });
        await uploadToGCS(data.upload_url, file, file.type || 'application/octet-stream',
          (p) => patchItem(id, { progress: p }));

        patchItem(id, { status: 'notifying' });
        const notifyToken = await auth.currentUser.getIdToken();
        const notifyRes = await fetch(NOTIFY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_token: notifyToken, gcs_path: data.gcs_path, bucket: data.bucket,
            filename: file.name, mime_type: file.type || 'application/octet-stream',
            size_bytes: file.size, domain: 'smartfinance', data_class: 'restricted',
          }),
        });
        const notifyData = await notifyRes.json().catch(() => ({}));
        const fileId = notifyData.file_id || '';
        patchItem(id, { status: 'uploaded', fileId });

        if (fileId) {
          attemptExtract(id, fileId, 0);
        } else {
          patchItem(id, {
            status: 'extract_error',
            error: "Uploaded, but no file ID came back - open it in SmartData to copy the File ID, then use Advanced entry below.",
          });
        }
      } catch (e) {
        patchItem(id, { status: 'upload_error', error: e.message || 'Upload failed.' });
      }
    }
  }

  async function accept(id, entryId) {
    if (!entryId?.trim()) return;
    patchItem(id, { status: 'confirming', error: null });
    try {
      const { ok, status, data } = await callSmartFinance('sf-confirm-entry-http', { entry_id: entryId.trim() });
      if (ok) {
        patchItem(id, { status: 'confirmed', error: null });
        onConfirmed?.();
      } else if (status === 409) {
        patchItem(id, { status: 'confirmed', error: null });
      } else {
        patchItem(id, { status: 'confirm_error', error: data?.error || 'Could not confirm.' });
      }
    } catch (e) {
      patchItem(id, { status: 'confirm_error', error: 'Network error.' });
    }
  }

  const STATUS_LABEL = {
    getting_url: 'preparing…', uploading: 'uploading…', notifying: 'flagging for SmartFinance…',
    uploaded: 'queued…', extracting: 'reading document…', processing: 'reading document…',
    upload_error: 'upload failed', extract_error: "couldn't extract",
    confirming: 'accepting…', confirm_error: "couldn't confirm",
  };

  return (
    <div className="sf-panel sf-intake-panel">
      <div className="sf-panel-header">
        <h3>📥 New invoices &amp; receipts</h3>
        <button className="sf-btn" onClick={() => inputRef.current?.click()}>Choose files</button>
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
               onChange={(e) => e.target.files.length && handleFiles(e.target.files)} />
      </div>
      <p className="sf-panel-hint">
        Drop invoices or receipts here. Each one is read automatically - accept the draft when it looks right.
      </p>

      {items.length === 0 && <p className="sf-panel-hint sf-dim">No files in this session yet.</p>}

      {items.map((it) => (
        <div key={it.id} className={`sf-intake-item sf-intake-${it.status}`}>
          <div className="sf-intake-row">
            <span className="sf-intake-name">{it.name}</span>
            <span className="sf-intake-status">
              {it.status === 'uploading' ? `uploading ${it.progress}%…` : STATUS_LABEL[it.status] || it.status}
            </span>
          </div>

          {it.status === 'extract_error' && it.error && <div className="sf-panel-error">{it.error}</div>}
          {it.status === 'upload_error' && it.error && <div className="sf-panel-error">{it.error}</div>}
          {it.status === 'confirm_error' && it.error && <div className="sf-panel-error">{it.error}</div>}

          {it.status === 'extract_error' && (
            <button className="sf-btn sf-btn-small" onClick={() => attemptExtract(it.id, it.fileId, 0)}>Try again</button>
          )}

          {it.status === 'draft_ready' && it.draft && (
            <div className="sf-draft-card">
              <DraftSummary draft={it.draft} />
              <button className="sf-btn" onClick={() => accept(it.id, it.entryId)} disabled={!it.entryId}>
                ✅ Accept
              </button>
            </div>
          )}

          {it.status === 'confirmed' && (
            <div className="sf-panel-hint">Accepted - reload Statement above to see it counted.</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Renders known draft fields nicely; anything unrecognised falls back to raw JSON
// so nothing is hidden even if the real shape has fields we haven't seen yet.
function DraftSummary({ draft }) {
  const known = ['vendor', 'amount', 'currency', 'date', 'description', 'category'];
  const hasKnown = known.some((k) => draft?.[k] !== undefined);
  if (!hasKnown) return <pre className="sf-panel-result">{JSON.stringify(draft, null, 2)}</pre>;
  return (
    <div className="sf-draft-summary">
      {draft.vendor && <div><strong>{draft.vendor}</strong></div>}
      {(draft.amount !== undefined) && (
        <div className="sf-draft-amount">{draft.currency || ''} {draft.amount}</div>
      )}
      {draft.date && <div className="sf-panel-hint">{draft.date}</div>}
      {draft.category && <div className="sf-panel-hint">{draft.category}</div>}
      {draft.description && <div className="sf-panel-hint">{draft.description}</div>}
    </div>
  );
}

// ── Advanced / manual entry - collapsed by default, for edge cases ─────────
// Not for normal use - the flow above does Extract/Confirm automatically.
// Kept for support/debugging: if a file was uploaded some other way, or the
// automatic flow needs to be re-run against a known ID by hand.
function AdvancedPanel({ clientGid }) {
  const [open, setOpen] = useState(false);
  const [fileId, setFileId] = useState('');
  const [entryId, setEntryId] = useState('');
  const [extractResult, setExtractResult] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);
  const [error, setError] = useState(null);

  async function extract() {
    setError(null); setExtractResult(null);
    const body = { file_id: fileId.trim() };
    if (clientGid?.trim()) body.client_gid = clientGid.trim();
    const { ok, data } = await callSmartFinance('sf-extract-http', body);
    if (ok) { setExtractResult(data); if (data.entry_id) setEntryId(data.entry_id); }
    else setError(data?.error || data?.reason || 'Could not extract.');
  }

  async function confirm() {
    setError(null); setConfirmResult(null);
    const { ok, data } = await callSmartFinance('sf-confirm-entry-http', { entry_id: entryId.trim() });
    if (ok) setConfirmResult(data);
    else setError(data?.error || 'Could not confirm.');
  }

  return (
    <div className="sf-panel sf-panel-inert">
      <div className="sf-panel-header">
        <h3>🛠️ Advanced / manual entry</h3>
        <button className="sf-btn sf-btn-small" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <>
          <p className="sf-panel-hint">
            For support and edge cases only - re-run Extract or Confirm by hand against a known ID.
            The Intake flow above already does this automatically for normal use.
          </p>
          <div className="sf-ask-row">
            <input type="text" value={fileId} onChange={(e) => setFileId(e.target.value)} placeholder="File ID" className="sf-ask-input" />
            <button className="sf-btn" onClick={extract} disabled={!fileId.trim()}>Extract</button>
          </div>
          {extractResult && <pre className="sf-panel-result">{JSON.stringify(extractResult, null, 2)}</pre>}
          <div className="sf-ask-row" style={{ marginTop: 8 }}>
            <input type="text" value={entryId} onChange={(e) => setEntryId(e.target.value)} placeholder="Entry ID" className="sf-ask-input" />
            <button className="sf-btn" onClick={confirm} disabled={!entryId.trim()}>Confirm</button>
          </div>
          {confirmResult && <pre className="sf-panel-result">{JSON.stringify(confirmResult, null, 2)}</pre>}
          {error && <div className="sf-panel-error">{error}</div>}
        </>
      )}
    </div>
  );
}

function SmartFinance({ gido, onBack }) {
  const [period, setPeriod] = useState(defaultPeriod());
  const [mode, setMode]     = useState('own'); // 'own' | 'client'
  const [clientGid, setClientGid] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);

  const effectiveClientGid = mode === 'client' ? clientGid : '';

  return (
    <div className="portal sf-page">
      <header className="portal-header">
        <div className="header-left">
          <BackButton onClick={onBack} />
          <span className="header-title">SmartFinance</span>
        </div>
        <div className="header-right"><span className="gido-badge">{gido?.gido_id}</span></div>
      </header>

      <div className="welcome-section">
        <h1><span className="gold">SmartFinance</span></h1>
        <p className="welcome-subtitle">Cash-basis accounting &amp; immutable ledger</p>
      </div>

      <div className="section">
        <ClientScopeBar gidoId={gido?.gido_id} mode={mode} setMode={setMode} clientGid={clientGid} setClientGid={setClientGid} />
      </div>

      <div className="section">
        <PeriodPicker from={period.from} to={period.to} onChange={setPeriod} />
      </div>

      <div className="section sf-grid">
        <StatementPanel from={period.from} to={period.to} clientGid={effectiveClientGid} key={refreshNonce} />
        <AskPanel from={period.from} to={period.to} />
        <IntakePanel clientGid={effectiveClientGid} onConfirmed={() => setRefreshNonce((n) => n + 1)} />
      </div>

      <div className="section">
        <AdvancedPanel clientGid={effectiveClientGid} />
      </div>
    </div>
  );
}

export default SmartFinance;